import { useState } from 'react';
import { useNavigate, Link, useSearch } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { useToast } from '@/components/ui/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LogoIcon } from '@/components/ui/Logo';
import { Loader2, AlertCircle, Lock, ArrowLeft } from 'lucide-react';

export default function ResetPassword() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();

  // Read token from query string: /reset-password?token=...
  const search = useSearch({ strict: false }) as { token?: string };
  const token = search.token ?? '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const passwordStrong = password.length >= 8;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError(t('auth:resetPassword.invalidToken'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('auth:resetPassword.mismatch'));
      return;
    }

    setIsLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      toast.success(t('auth:resetPassword.success'));
      navigate({ to: '/login' });
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      setError(msg || t('auth:resetPassword.invalidToken'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex bg-[var(--color-base)] text-[var(--color-text)]">
      {/* ── Left: form pane ── */}
      <div className="relative w-full lg:w-[480px] flex flex-col p-8 lg:p-12 z-10 bg-[var(--color-base)] shadow-[20px_0_40px_rgba(0,0,0,0.3)]">
        {/* Brand pinned top */}
        <button
          onClick={() => navigate({ to: '/' })}
          className="flex items-center gap-1.5 self-start"
          aria-label={t('a11y:backHome')}
        >
          <LogoIcon size="lg" />
          {/* eslint-disable-next-line i18next/no-literal-string */}
          <span className="font-logo text-2xl leading-none">my360</span>
        </button>

        {/* Form vertically centered */}
        <div className="flex-1 flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
          >
            <h1 className="text-3xl font-semibold mb-2 tracking-tight">
              {t('auth:resetPassword.title')}
            </h1>
            <p className="text-[var(--color-text-muted)] mb-8 max-w-sm">
              {t('auth:resetPassword.subtitle')}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 rounded-lg border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 p-3 text-sm text-[var(--color-destructive)] animate-shake">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <div className="space-y-1">
                <label className="text-[10px] font-medium text-[var(--color-text-muted)] uppercase tracking-widest pl-1">
                  {t('auth:resetPassword.password')}
                </label>
                <Input
                  type="password"
                  placeholder={t('auth:resetPassword.passwordPlaceholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  icon={<Lock className="w-4 h-4" />}
                  required
                  autoComplete="new-password"
                  minLength={8}
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-medium text-[var(--color-text-muted)] uppercase tracking-widest pl-1">
                  {t('auth:resetPassword.confirmPassword')}
                </label>
                <Input
                  type="password"
                  placeholder={t('auth:resetPassword.confirmPasswordPlaceholder')}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  icon={<Lock className="w-4 h-4" />}
                  required
                  autoComplete="new-password"
                />
              </div>

              <Button
                type="submit"
                variant="primary"
                disabled={isLoading || !passwordStrong}
                className="w-full h-11 mt-6"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('auth:resetPassword.resetting')}
                  </>
                ) : (
                  t('auth:resetPassword.submit')
                )}
              </Button>
            </form>

            <div className="mt-8 text-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                {t('auth:resetPassword.backToLogin')}
              </Link>
            </div>
          </motion.div>
        </div>
      </div>

      {/* ── Right: ambient pane ── */}
      <div className="hidden lg:block relative flex-1 bg-[var(--color-surface)] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2670&auto=format&fit=crop"
          alt=""
          className="w-full h-full object-cover opacity-30 animate-[pulse_10s_ease-in-out_infinite]"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[var(--color-base)] opacity-50" />
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 100, repeat: Infinity, ease: 'linear' }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-white/5 rounded-full pointer-events-none"
        />
        <motion.div
          animate={{ rotate: -360 }}
          transition={{ duration: 150, repeat: Infinity, ease: 'linear' }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-[var(--color-primary)]/10 rounded-full pointer-events-none"
        />
      </div>
    </div>
  );
}
